/**
 * Owns the "Find more like this" exemplar-drawing state (FewShotExemplarState, defined in
 * components/AgentExplorer since AgentPanel's sidebar UI needs the type too) at the MPV view
 * level, alongside useOrderImageryFlow, since the map and the sidebar are siblings and neither
 * one owns the other.
 */

import * as turf from '@turf/turf';
import * as React from 'react';

import {
  DEFAULT_FEW_SHOT_EXEMPLAR_STATE,
  ExemplarPolarity,
  ExemplarProblem,
  ExemplarRef,
  FewShotExemplarFlow,
  FewShotExemplarState,
  getExemplars,
  isSameExemplar,
} from 'app/components/AgentExplorer/fewShotExemplarTypes';
import * as C from 'app/utils/constants';
import {intersectGeometries} from 'app/utils/geoJsonUtils';

// Width and height in meters of a geometry's bbox. Geodesic rather than the server's UTM
// projection; the two agree to well under 1% at property scale.
function bboxSidesMeters(geometry: GeoJSON.Geometry): [number, number] {
  const [minX, minY, maxX, maxY] = turf.bbox(geometry);
  const midY = (minY + maxY) / 2;
  const width = turf.distance([minX, midY], [maxX, midY], {units: 'meters'});
  const height = turf.distance([minX, minY], [minX, maxY], {units: 'meters'});
  return [width, height];
}

// The shortest box side the server accepts: 2 source pixels, and enough pixels to survive the
// coarse pass reading the whole property as one capped overview.
export function minExemplarSideMeters(
  featureGeometry: GeoJSON.Geometry,
  sceneResolution: number | null
): number {
  const spanM = Math.max(...bboxSidesMeters(featureGeometry));
  const propertyMin =
    (spanM * C.AGENT_FEW_SHOT_MIN_EXEMPLAR_PX) / C.AGENT_FEW_SHOT_OVERVIEW_SIZE_CAP;
  const imageryMin =
    sceneResolution === null ? 0 : C.AGENT_FEW_SHOT_MIN_SIDE_PIXELS * sceneResolution;
  return Math.max(propertyMin, imageryMin);
}

export function getExemplarProblem(
  exemplar: GeoJSON.Polygon,
  featureGeometry: GeoJSON.Geometry,
  sceneResolution: number | null
): ExemplarProblem | null {
  if (!turf.booleanIntersects(exemplar, featureGeometry)) {
    return 'outside';
  }
  const sideM = Math.min(...bboxSidesMeters(exemplar));
  if (sceneResolution !== null && sideM < C.AGENT_FEW_SHOT_MIN_SIDE_PIXELS * sceneResolution) {
    return 'too-small-imagery';
  }
  if (turf.area(exemplar) > C.AGENT_FEW_SHOT_MAX_AREA_FRACTION * turf.area(featureGeometry)) {
    return 'too-big';
  }
  if (sideM < minExemplarSideMeters(featureGeometry, null)) {
    return 'too-small-property';
  }
  return null;
}

function intersectionOverUnion(a: GeoJSON.Polygon, b: GeoJSON.Polygon): number {
  const intersection = intersectGeometries(a, b);
  if (!intersection) {
    return 0;
  }
  const overlap = turf.area(intersection);
  return overlap / (turf.area(a) + turf.area(b) - overlap);
}

// An ignore box drawn over an example contradicts it; the server refuses the run.
export function matchesAnExample(negative: GeoJSON.Polygon, exemplars: GeoJSON.Polygon[]): boolean {
  return exemplars.some(
    (exemplar) => intersectionOverUnion(negative, exemplar) > C.AGENT_FEW_SHOT_DUPLICATE_IOU
  );
}

function withExemplars(
  state: FewShotExemplarState,
  polarity: ExemplarPolarity,
  exemplars: GeoJSON.Polygon[]
): FewShotExemplarState {
  return polarity === 'positive' ? {...state, exemplars} : {...state, negativeExemplars: exemplars};
}

// featureGeometry is null when no property is selected; nothing is checked then and the server
// stays the only gate.
export default function useFewShotExemplars(
  featureGeometry: GeoJSON.Geometry | null,
  sceneResolution: number | null,
  zoomToGeometry?: (geometry: GeoJSON.Geometry) => void
): FewShotExemplarFlow {
  const [fewShotState, setFewShotState] = React.useState<FewShotExemplarState>(
    DEFAULT_FEW_SHOT_EXEMPLAR_STATE
  );

  const exemplarProblems = React.useMemo(() => {
    const problemsOf = (exemplars: GeoJSON.Polygon[]) =>
      exemplars.map((exemplar) =>
        featureGeometry ? getExemplarProblem(exemplar, featureGeometry, sceneResolution) : null
      );
    return {
      positive: problemsOf(fewShotState.exemplars),
      negative: problemsOf(fewShotState.negativeExemplars).map((problem, index) =>
        problem === null &&
        featureGeometry &&
        matchesAnExample(fewShotState.negativeExemplars[index], fewShotState.exemplars)
          ? 'matches-example'
          : problem
      ),
    };
  }, [fewShotState.exemplars, fewShotState.negativeExemplars, featureGeometry, sceneResolution]);

  const setFewShotLabel = React.useCallback(
    (label: string) => setFewShotState((prev) => ({...prev, label})),
    []
  );

  const setSearchTerm = React.useCallback(
    (searchTerm: string) => setFewShotState((prev) => ({...prev, searchTerm})),
    []
  );

  const setNegativeLabel = React.useCallback(
    (negativeLabel: string) => setFewShotState((prev) => ({...prev, negativeLabel})),
    []
  );

  const startDrawingExemplar = React.useCallback(
    (polarity: ExemplarPolarity) =>
      setFewShotState((prev) =>
        getExemplars(prev, polarity).length >= C.AGENT_FEW_SHOT_MAX_EXEMPLARS
          ? prev
          : {...prev, drawing: true, drawingPolarity: polarity, editing: null}
      ),
    []
  );

  const stopDrawingExemplar = React.useCallback(
    () => setFewShotState((prev) => (prev.drawing ? {...prev, drawing: false} : prev)),
    []
  );

  // Draw mode stays on between boxes so each one is a single drag, and ends when the list being
  // drawn into hits its cap, where the next box would be dropped.
  const addExemplar = React.useCallback(
    (polygon: GeoJSON.Polygon) =>
      setFewShotState((prev) => {
        const exemplars = getExemplars(prev, prev.drawingPolarity);
        return exemplars.length >= C.AGENT_FEW_SHOT_MAX_EXEMPLARS
          ? {...prev, drawing: false}
          : {
              ...withExemplars(prev, prev.drawingPolarity, [...exemplars, polygon]),
              drawing: exemplars.length + 1 < C.AGENT_FEW_SHOT_MAX_EXEMPLARS,
            };
      }),
    []
  );

  const updateExemplar = React.useCallback(
    ({polarity, index}: ExemplarRef, polygon: GeoJSON.Polygon) =>
      setFewShotState((prev) =>
        withExemplars(
          prev,
          polarity,
          getExemplars(prev, polarity).map((exemplar, i) => (i === index ? polygon : exemplar))
        )
      ),
    []
  );

  // A removed chip never fires mouseleave, so hover is dropped here rather than left pointing at
  // whichever box shifted into that index. Editing is dropped for the same reason.
  const removeExemplar = React.useCallback(
    ({polarity, index}: ExemplarRef) =>
      setFewShotState((prev) => ({
        ...withExemplars(
          prev,
          polarity,
          getExemplars(prev, polarity).filter((_, i) => i !== index)
        ),
        hovered: null,
        editing: null,
      })),
    []
  );

  const clearExemplars = React.useCallback(
    () =>
      setFewShotState((prev) => ({
        ...prev,
        exemplars: [],
        negativeExemplars: [],
        hovered: null,
        editing: null,
      })),
    []
  );

  const startEditingExemplar = React.useCallback(
    (ref: ExemplarRef) =>
      setFewShotState((prev) =>
        isSameExemplar(prev.editing, ref) ? prev : {...prev, drawing: false, editing: ref}
      ),
    []
  );

  const focusExemplar = React.useCallback(
    (ref: ExemplarRef) => {
      const exemplar = getExemplars(fewShotState, ref.polarity)[ref.index];
      if (!exemplar) {
        return;
      }
      zoomToGeometry?.(exemplar);
      startEditingExemplar(ref);
    },
    [fewShotState, zoomToGeometry, startEditingExemplar]
  );

  // Keyed on the ref so a deselect from the box being left can't close a box just opened by the
  // same click.
  const stopEditingExemplar = React.useCallback(
    (ref?: ExemplarRef, resumeDrawing = false) =>
      setFewShotState((prev) =>
        prev.editing !== null && (ref === undefined || isSameExemplar(prev.editing, ref))
          ? {
              ...prev,
              editing: null,
              drawing:
                resumeDrawing &&
                getExemplars(prev, prev.drawingPolarity).length < C.AGENT_FEW_SHOT_MAX_EXEMPLARS,
            }
          : prev
      ),
    []
  );

  const resetFewShot = React.useCallback(
    () => setFewShotState(DEFAULT_FEW_SHOT_EXEMPLAR_STATE),
    []
  );

  const setHoveredExemplar = React.useCallback(
    (ref: ExemplarRef | null) =>
      setFewShotState((prev) =>
        prev.hovered === ref || isSameExemplar(prev.hovered, ref) ? prev : {...prev, hovered: ref}
      ),
    []
  );

  return React.useMemo(
    () => ({
      fewShotState,
      exemplarProblems,
      setHoveredExemplar,
      setFewShotLabel,
      setSearchTerm,
      setNegativeLabel,
      startDrawingExemplar,
      stopDrawingExemplar,
      addExemplar,
      updateExemplar,
      removeExemplar,
      startEditingExemplar,
      focusExemplar,
      stopEditingExemplar,
      clearExemplars,
      resetFewShot,
    }),
    [
      fewShotState,
      exemplarProblems,
      setHoveredExemplar,
      setFewShotLabel,
      setSearchTerm,
      setNegativeLabel,
      startDrawingExemplar,
      stopDrawingExemplar,
      addExemplar,
      updateExemplar,
      removeExemplar,
      startEditingExemplar,
      focusExemplar,
      stopEditingExemplar,
      clearExemplars,
      resetFewShot,
    ]
  );
}
